import HELPER from '@/app/utils/HELPER';
import { computed, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import type { TFeedbackStore, TFeedbackQuestion } from '../types';
import { MODULE_NAME } from '../constants';
import { MUTATION_TYPES, ACTION_TYPES } from '../store/storeTypes';

export const useFeedback = (onClose: Function, feedbackType: string) => {
  const store = useStore<{ feedback: TFeedbackStore }>();
  const feedbackStore = computed(() => store.state.feedback);
  const feedbackHeight = ref(0);
  const isDone = ref(false);
  const questionActive = computed<TFeedbackQuestion>(() => {
    return feedbackStore.value?.questionActive;
  });
  const firstQuestion = computed<TFeedbackQuestion>(() => {
    return feedbackStore.value?.firstQuestionActive;
  });
  const feedbackFormActive = computed<TFeedbackQuestion>(() => {
    return feedbackStore.value?.feedbackFormActive;
  });

  const activeQuestionIndex = computed(() => {
    const { questions } = feedbackStore.value;
    return questions.findIndex((item) => item.id === questionActive.value?.id);
  });

  const totalStep = computed(() => {
    return feedbackStore.value.totalStep;
  });

  const isSubmitLoading = computed(() => {
    return feedbackStore.value.period.submitLoading;
  });

  const isLatestQuestion = computed(() => {
    return !questionActive.value?.next;
  });

  const isFetchingQuestions = computed(() => {
    return feedbackStore.value?.period.questionsLoading;
  });

  const getResultByQuestion = (questionId: number) => {
    return feedbackStore.value?.results[questionId];
  };

  const disableNextButton = computed(() => {
    const currentResult = getResultByQuestion(questionActive.value?.id);
    // HANDLE
    if (!questionActive.value) {
      return true;
    }
    if (questionActive.value?.validate === 'NOT_NULL') {
      if (!currentResult?.answer || (Array.isArray(currentResult?.answer) && currentResult?.answer.length === 0)) {
        return true;
      }
      if (Array.isArray(currentResult?.answer)) {
        const itemValidates = currentResult?.answer?.filter((item: any) => {
          const option = questionActive.value.options.find(
            (opt) => opt.key === item.key && opt.validate === 'NOT_NULL',
          );
          return !!option;
        });
        let isValid = true;
        itemValidates.forEach((item) => {
          if (!item.value?.trim()) {
            isValid = false;
          }
        });

        return !isValid;
      }

      return false;
    }
    return false;
  });

  const onSubmit = async () => {
    if (isSubmitLoading.value) {
      return;
    }
    const { results } = feedbackStore.value;
    const keys = Object.keys(results);
    const resultsPayload = keys.map((key) => {
      const item = results[key];
      return {
        question_id: item.questionId,
        question_type: item.questionType,
        answer: Array.isArray(item.answer) ? item.answer : [item.answer],
      };
    });

    try {
      await store.dispatch(`${MODULE_NAME}/${ACTION_TYPES.SUBMIT_FEEDBACK}`, {
        feedback_form_id: feedbackFormActive.value?.id || 1,
        answers: resultsPayload,
      });
      isDone.value = true;
      setTimeout(() => {
        collapseSection();
      }, 1);
      // onClose();
      // HELPER.showNotify('Your feedback is submitted. Thanks for sharing with us!', 'success');
    } catch (error) {
      HELPER.showNotify('Error.', 'error');
    }
  };

  const onGoBack = () => {
    if (activeQuestionIndex.value === 0) {
      return;
    }
    const preQuestionIndex = questionActive.value.previous - 1;
    const preQuestion = feedbackStore.value.questions[preQuestionIndex];
    // store.commit(`${MODULE_NAME}/${MUTATION_TYPES.UPDATE_RESULT}`, {
    //   questionId: questionActive.value.id,
    //   answer: null,
    // });
    store.commit(`${MODULE_NAME}/${MUTATION_TYPES.UPDATE_QUESTION_ACTIVE}`, preQuestion);
    setTimeout(() => {
      collapseSection();
    }, 1);
  };

  const onNext = (question?: any) => {
    // if (disableNextButton.value) {
    //   return;
    // }
    // if (isLatestQuestion.value) {
    //   onSubmit();
    //   return;
    // }
    // HANDLE
    let questionNextIndex: any = (question || questionActive.value).next;
    const next = (question || questionActive.value).next;
    if (typeof next === 'object') {
      const currentResult = getResultByQuestion((question || questionActive.value)?.id);

      const answerKey = currentResult.answer?.key;
      questionNextIndex = Number(next[answerKey]) - 1;
    }
    let nextQuestion = feedbackStore.value.questions[questionNextIndex];
    store.commit(`${MODULE_NAME}/${MUTATION_TYPES.UPDATE_QUESTION_ACTIVE}`, nextQuestion);
    setTimeout(() => {
      collapseSection();
    }, 1);
  };

  const clearStore = () => {
    store.commit(`${MODULE_NAME}/${MUTATION_TYPES.CLEAR_STORE}`);
  };

  const buttonText = computed(() => {
    return isLatestQuestion.value ? 'Submit' : 'Next';
  });

  const getQuestion = async () => {
    try {
      await store.dispatch(`${MODULE_NAME}/${ACTION_TYPES.FETCH_FEEDBACK_FORM}`, feedbackType);
    } catch (error) {
      onClose();
    }
  };

  watchEffect(() => {
    if (firstQuestion.value) {
      setTimeout(() => {
        let elWrap = document.getElementById('feedback-form');
        if (elWrap) {
          let wrect = elWrap.getBoundingClientRect();
          feedbackHeight.value = wrect.height;
          elWrap.style.height = feedbackHeight.value + 'px';
        }
      }, 0);
    }
  });

  function collapseSection(isScroll?: boolean) {
    const elWrap = document.getElementById('feedback-form');
    const wrect = elWrap.getBoundingClientRect();
    const scrollElemet = document.getElementById('feedback-scroll');
    const prevHeight = wrect.height + 'px';
    elWrap.style.height = 'auto';
    let rect = elWrap.getBoundingClientRect();
    const newHeight = rect.height;
    elWrap.style.height = prevHeight;

    requestAnimationFrame(() => {
      elWrap.style.height = newHeight + 'px';
      if (isScroll) {
        scrollElemet.scrollTop = newHeight;
      }
    });
  }

  return {
    getResultByQuestion,
    questionActive,
    onNext,
    activeQuestionIndex,
    isLatestQuestion,
    disableNextButton,
    totalStep,
    clearStore,
    isSubmitLoading,
    onGoBack,
    buttonText,
    isFetchingQuestions,
    getQuestion,
    firstQuestion,
    onSubmit,
    collapseSection,
    isDone,
  };
};
